// LeftBar.tsx
import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  type Variants,
  useAnimation,
} from "framer-motion";

import MenuIcon from "../assets/icons/menu.svg";

import {
  Package,
  Boxes,
  LogOut,
  ShoppingCart,
  AlertTriangle,
  PauseCircle,
  ChevronRight,
  Dot,
  Wrench,
  Building2,
  Ship,
  Users,
  FileText,
  ClipboardList,
  Bug,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/auth-context";

/* ============================
 * Tipagens
 * ============================ */
type User = {
  id: number;
  nome?: string;
  email?: string;
  departamento?: string;
};

type SubItem = {
  label: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
};

type MainItem = {
  label: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  subitems: SubItem[];
};

type Department = {
  name: string;
  items: MainItem[];
  slug?: string; // ✅ chave de permissão/visibilidade (ex: "tecnicos")
  routeKey?: string; // ✅ chave de rota/URL (ex: "Tecnicos&Laboratorio")
  ordem?: number | null;
};

const LEFTBAR_CLASS = "eva-leftbar";

/* ============================
 * Estilos base
 * ============================ */
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "250px",
    height: "100vh",
    backgroundColor: "#124280",
    zIndex: 1200,
    boxSizing: "border-box",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    transition: "transform 0.3s ease-in-out",
    overflowY: "auto",
    userSelect: "none",
  },
  inner: { padding: "10px 0" },
  header: { padding: "0 22px", marginBottom: "20px" },
  title: { fontSize: "17px", fontWeight: 500 },
  departments: {
    padding: "10px 22px",
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    position: "relative",
  },
  submenuWrapper: { marginBottom: "14px" },
  submenu: { display: "flex", flexDirection: "column" },
  subItem: {
    display: "flex",
    alignItems: "center",
    height: "34px",
    fontSize: "12px",
    fontWeight: 300,
    cursor: "pointer",
    padding: "0 22px",
    position: "relative",
    overflow: "hidden",
  },
  subItemContent: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
  },
  subItemActive: {
    backgroundColor: "#4280DD51",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 1100,
  },
  mobileHeader: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "60px",
    backgroundColor: "#124280",
    display: "flex",
    alignItems: "center",
    padding: "0 15px",
    justifyContent: "space-between",
    color: "#fff",
    zIndex: 1300,
  },
  titleMobile: {
    flex: 1,
    fontSize: "17px",
    fontWeight: 500,
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif",
    paddingRight: "30px",
  },
  migrationMarksWrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    marginLeft: "auto",
  },
  migrationMark: {
    width: 3,
    height: 18,
    borderRadius: 999,
    background: "rgba(16,185,129,0.95)", // verde
    boxShadow: "0 0 0 1px rgba(6,95,70,0.35)",
  },
  migrationMarkOrange: {
    width: 3,
    height: 18,
    borderRadius: 999,
    background: "rgba(249,115,22,0.95)", // laranja
    boxShadow: "0 0 0 1px rgba(154,52,18,0.35)",
  },
  migrationMarkBlue: {
    width: 3,
    height: 18,
    borderRadius: 999,
    background: "rgba(59,130,246,0.95)", // azul
    boxShadow: "0 0 0 1px rgba(30,64,175,0.35)",
  },
};

/* ============================
 * Badge DEV
 * ============================ */
const DevBadge: React.FC = () => (
  <span
    title="Ainda não finalizado"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: 6,
      borderRadius: 6,
      padding: "1px 4px",
      background: "rgba(255, 200, 0, 0.25)",
      border: "1px solid rgba(255, 200, 0, 0.6)",
    }}
  >
    <Wrench size={12} style={{ color: "#FFD34D" }} />
  </span>
);

const MAIN_THAT_GO_TO_FIRST_SUB = new Set(["saidas"]);

/* ============================
 * Fallback estático (caso API falhe/sem token)
 * ============================ */
const departmentData: Department[] = [
  {
    name: "Suprimentos",
    slug: "suprimentos",
    routeKey: "suprimentos",
    items: [
      {
        label: "Produtos",
        devOnly: false,
        developed: true,
        migrated: true,
        markOrange: false,
        markBlue: false,
        subitems: [
          {
            label: "Registrar Produto",
            devOnly: false,
            developed: true,
            migrated: false,
            markOrange: false,
            markBlue: false,
          },
        ],
      },
      {
        label: "Estoque",
        devOnly: false,
        developed: true,
        migrated: false,
        markOrange: false,
        markBlue: false,
        subitems: [
          {
            label: "Realizar Movimentação",
            devOnly: false,
            developed: true,
            migrated: false,
            markOrange: false,
            markBlue: false,
          },
          {
            label: "Entradas",
            devOnly: false,
            developed: true,
            migrated: false,
            markOrange: false,
            markBlue: false,
          },
          {
            label: "Ajustes",
            devOnly: false,
            developed: true,
            migrated: false,
            markOrange: false,
            markBlue: false,
          },
          {
            label: "Retornos",
            devOnly: false,
            developed: true,
            migrated: false,
            markOrange: false,
            markBlue: false,
          },
        ],
      },
      {
        label: "Saídas",
        devOnly: false,
        developed: true,
        migrated: false,
        markOrange: false,
        markBlue: false,
        subitems: [
          {
            label: "Serviços",
            devOnly: false,
            developed: true,
            migrated: false,
          },
          { label: "Vendas", devOnly: false, developed: true, migrated: false },
          {
            label: "Consumos",
            devOnly: false,
            developed: true,
            migrated: false,
          },
          { label: "Perdas", devOnly: false, developed: true, migrated: false },
        ],
      },
      {
        label: "Standby-Serviço",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby-Comercial",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby-Consumo",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
    ],
  },
  {
    name: "Coordenação",
    slug: "coordenacao",
    routeKey: "coordenacao",
    items: [
      {
        label: "Produtos",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Saidas",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Colaboradores",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [
          {
            label: "Visualizar Mapa",
            devOnly: true,
            developed: false,
            migrated: false,
          },
        ],
      },
      {
        label: "Empresas Cadastradas",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Embarcações",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
    ],
  },
  {
    name: "Comercial",
    slug: "comercial",
    routeKey: "comercial",
    items: [
      {
        label: "Solicitações",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Produtos",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Saidas",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
    ],
  },
  {
    name: "Recursos Humanos",
    slug: "recursos-humanos",
    routeKey: "recursos-humanos",
    items: [
      {
        label: "Empresas Cadastradas",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Colaboradores",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Controle de Ponto",
        devOnly: true,
        developed: false,
        migrated: false,
        markOrange: true,
        subitems: [],
      },
      {
        label: "D. Empresa",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Produtos",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [
          {
            label: "Registrar Produto",
            devOnly: false,
            developed: true,
            migrated: false,
          },
        ],
      },
      {
        label: "Estoque",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [
          {
            label: "Realizar Movimentação",
            devOnly: false,
            developed: true,
            migrated: false,
          },
        ],
      },
      {
        label: "Saídas",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [
          {
            label: "Consumos",
            devOnly: false,
            developed: true,
            migrated: false,
          },
          { label: "Perdas", devOnly: false, developed: true, migrated: false },
        ],
      },
      {
        label: "Standby",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
    ],
  },

  // ✅ O seu caso real:
  // slug (permissão): "tecnicos"
  // routeKey (rota/URL): "Tecnicos&Laboratorio"
  {
    name: "Técnicos&Laboratório",
    slug: "tecnicos",
    routeKey: "Tecnicos&Laboratorio",
    items: [
      {
        label: "Produtos",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [
          {
            label: "Registrar Produto",
            devOnly: true,
            developed: false,
            migrated: false,
          },
        ],
      },
      {
        label: "Estoque",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [
          {
            label: "Realizar Movimentação",
            devOnly: true,
            developed: false,
            migrated: false,
          },
        ],
      },
      {
        label: "Saídas",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
    ],
  },

  {
    name: "Diretoria",
    slug: "diretoria",
    routeKey: "diretoria",
    items: [
      {
        label: "Estoque-Suprimentos",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Saidas-RH",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Saidas-Consumo",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Saidas-Servico",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Saidas-Comercial",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby-RH",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby-Consumo",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby-Servico",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
      {
        label: "Standby-Comercial",
        devOnly: false,
        developed: true,
        migrated: false,
        subitems: [],
      },
    ],
  },
  {
    name: "Suporte",
    slug: "suporte",
    routeKey: "suporte",
    items: [
      {
        label: "Solicitações",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
      {
        label: "Problemas",
        devOnly: true,
        developed: false,
        migrated: false,
        subitems: [],
      },
    ],
  },
];

/* ============================
 * Helpers
 * ============================ */
const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const deptSlugFromName = (name: string) =>
  removeAccents(name).toLowerCase().replace(/\s+/g, "");
const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const canSeeFactory = (userDeptRaw?: string) => {
  const who = norm(String(userDeptRaw || "").trim()); // ex: "tecnicos"
  if (!who) return (_: string) => false;
  if (who === "desenvolvedor") return (_: string) => true;

  return (targetSlug: string) => {
    const target = norm(targetSlug);

    // ✅ permite que dept tecnico veja o módulo Técnicos&Laboratório
    if (target === "tecnicos&laboratorio") {
      return who === "tecnico" || who === "tecnicos" || who === "laboratorio";
    }

    return who === target;
  };
};

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [18, 66, 128];
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return [18, 66, 128];
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b))
    return [18, 66, 128];
  return [r, g, b];
}

/** ✅ chave de permissão/visibilidade */
const getDeptPermKey = (d: Department) => {
  // slug do banco (ex: "tecnicos")
  if (d.slug) return String(d.slug);
  // fallback: tenta derivar do nome
  return deptSlugFromName(d.name);
};

/** ✅ chave de rota/URL (não normaliza, preserva & e maiúsculas) */
const getDeptRouteKey = (d: Department) => {
  // routeKey do banco (ex: "Tecnicos&Laboratorio")
  if (d.routeKey) return String(d.routeKey);
  // fallback sensato: usa slug (se existir) senão deriva do nome
  if (d.slug) return String(d.slug);
  return deptSlugFromName(d.name);
};

/* ============================
 * Ícones
 * ============================ */
const OptionIcon: React.FC<{
  label: string;
  size?: number;
  className?: string;
}> = ({ label, size = 28, className }) => {
  const rawLower = label.trim().toLowerCase();
  const n = norm(label);
  const base = n.replace(/\s+/g, "-").split("-")[0];

  if (rawLower === "colaboradores")
    return (
      <Users size={size} className={className} style={{ marginRight: 13 }} />
    );

  if (
    rawLower === "d. empresa" ||
    rawLower === "documentacao da empresa" ||
    rawLower === "documentação da empresa"
  )
    return (
      <FileText size={size} className={className} style={{ marginRight: 13 }} />
    );

  if (rawLower === "solicitações" || rawLower === "solicitacoes")
    return (
      <ClipboardList
        size={size}
        className={className}
        style={{ marginRight: 13 }}
      />
    );

  if (rawLower === "problemas")
    return (
      <Bug size={size} className={className} style={{ marginRight: 13 }} />
    );

  if (base === "produtos")
    return (
      <Package size={size} className={className} style={{ marginRight: 13 }} />
    );
  if (base === "estoque")
    return (
      <Boxes size={size} className={className} style={{ marginRight: 13 }} />
    );
  if (base === "saidas" || base === "saídas")
    return (
      <LogOut size={size} className={className} style={{ marginRight: 13 }} />
    );
  if (base === "vendas")
    return (
      <ShoppingCart
        size={size}
        className={className}
        style={{ marginRight: 13 }}
      />
    );
  if (base === "perdas")
    return (
      <AlertTriangle
        size={size}
        className={className}
        style={{ marginRight: 13 }}
      />
    );
  if (base === "standby")
    return (
      <PauseCircle
        size={size}
        className={className}
        style={{ marginRight: 13 }}
      />
    );
  if (base === "empresas")
    return (
      <Building2
        size={size}
        className={className}
        style={{ marginRight: 13 }}
      />
    );
  if (base === "embarcacoes")
    return (
      <Ship size={size} className={className} style={{ marginRight: 13 }} />
    );
  if (base === "circle")
    return <Dot size={14} className={className} style={{ marginRight: 8 }} />;

  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        marginRight: 13,
      }}
    />
  );
};

/* ============================
 * Variants
 * ============================ */
const sidebarVariants: Variants = {
  hidden: { x: "-100%", opacity: 0 },
  show: {
    x: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 260, damping: 26 },
  },
};

const deptItemVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.02 * i,
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  }),
};

const submenuVariants: Variants = {
  collapsed: { height: 0, opacity: 0, transition: { duration: 0.18 } },
  open: {
    height: "auto",
    opacity: 1,
    transition: { type: "spring", stiffness: 250, damping: 26 },
  },
};

const hoverGlow: Variants = {
  rest: { scale: 1, x: 0, filter: "brightness(1)" },
  hover: {
    scale: 1.01,
    x: 2,
    filter: "brightness(1.05)",
    transition: { type: "spring", stiffness: 300, damping: 20 },
  },
};

const chevronVariants: Variants = {
  closed: { rotate: 0 },
  open: {
    rotate: 90,
    transition: { type: "spring", stiffness: 400, damping: 28 },
  },
};

const activeIndicatorVariants: Variants = {
  hidden: { scaleY: 0, opacity: 0 },
  show: {
    scaleY: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 400, damping: 24 },
  },
};

/* ============================
 * Componente
 * ============================ */
const LeftBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openDepts, setOpenDepts] = useState<string[]>([]);
  const [collapsedByUser, setCollapsedByUser] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [menuDepartments, setMenuDepartments] = useState<Department[] | null>(
    null
  );
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState<string | null>(null);

  const { theme, systemTitle } = useTheme();
  const { sessao } = useAuth();

  useReducedMotion();
  const sidebarControls = useAnimation();

  const userDept = (sessao?.departamento || "").toLowerCase();
  const isDev = userDept === "desenvolvedor";

  const primaryColor = theme?.primary || "#124280";
  const menuButtonsSelection = theme?.menu_buttons_selection || "#4280DD51";
  const menu_buttons_selection_effect =
    theme?.menu_buttons_selection_effect || "#4280DD51";
  hexToRgbComponents(primaryColor); // mantido (você não usa r,g,b aqui)

  // layout responsive
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1000;
      setIsMobile(mobile);
      setIsOpen(!mobile);
      sidebarControls.start(mobile ? "hidden" : "show");
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar menu da API
  useEffect(() => {
    let aborted = false;

    const loadMenu = async () => {
      if (menuDepartments) return;

      let token = "";
      try {
        if (typeof window !== "undefined") {
          token = localStorage.getItem("access_token") || "";
        }
      } catch {
        token = "";
      }

      if (!token) {
        setMenuDepartments(null);
        setMenuError("Token ausente");
        return;
      }

      try {
        setMenuLoading(true);
        setMenuError(null);

        const res = await fetch(
          "https://api.eva-googlemarine.com/geral/get-menu",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (!data?.ok || !Array.isArray(data.menu)) {
          throw new Error("Resposta de menu inválida");
        }

        if (aborted) return;

        const mapped: Department[] = data.menu.map((dept: any) => ({
          name: dept.name,
          slug: dept.slug,
          routeKey:
            dept.routeKey ??
            dept.route_key ??
            dept.routeKey ??
            dept.route_key ??
            dept.slug, // ✅
          ordem: dept.ordem,
          items: (dept.items || []).map((it: any) => ({
            label: it.label,
            devOnly: !!it.devOnly,
            developed: it.developed,
            migrated: it.migrated,
            markOrange: it.markOrange,
            markBlue: it.markBlue,
            subitems: (it.subitems || []).map((sub: any) => ({
              label: sub.label,
              devOnly: !!sub.devOnly,
              developed: sub.developed,
              migrated: sub.migrated,
              markOrange: sub.markOrange,
              markBlue: sub.markBlue,
            })),
          })),
        }));

        setMenuDepartments(mapped);
      } catch (e: any) {
        console.error("Erro ao carregar menu da API:", e);
        if (!aborted) {
          setMenuError(String(e?.message || e));
          setMenuDepartments(null);
        }
      } finally {
        if (!aborted) setMenuLoading(false);
      }
    };

    loadMenu();
    return () => {
      aborted = true;
    };
  }, [menuDepartments]);

  const canSeeDept = canSeeFactory(userDept);

  // Fonte final: API se ok, senão fallback estático
  const departmentsSource: Department[] =
    menuDepartments && !menuError ? menuDepartments : departmentData;

  // ✅ visibilidade por permKey (slug)
  const visibleDepartments = departmentsSource.filter((d) =>
    canSeeDept(getDeptPermKey(d))
  );

  const itemVisible = (item: MainItem) => (isDev ? true : !item.devOnly);
  const subItemVisible = (sub: SubItem) => (isDev ? true : !sub.devOnly);

  const renderLabelWithBadge = (
    label: string,
    developed?: boolean,
    migrated?: boolean,
    markOrange?: boolean,
    markBlue?: boolean
  ) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
      }}
    >
      <span>{label}</span>

      {isDev && developed === false ? <DevBadge /> : null}

      {isDev && (migrated || markOrange || markBlue) && (
        <span style={styles.migrationMarksWrapper}>
          {migrated && (
            <span
              style={styles.migrationMark}
              title="API desta tela já está migrada"
            />
          )}
          {markOrange && (
            <span
              style={styles.migrationMarkOrange}
              title="Status extra (laranja)"
            />
          )}
          {markBlue && (
            <span
              style={styles.migrationMarkBlue}
              title="Status extra (azul)"
            />
          )}
        </span>
      )}
    </span>
  );

  // ✅ rota inicial do dept: usa routeKey
  const buildFirstPath = (dept: Department) => {
    const baseDept = `/${getDeptRouteKey(dept)}`;
    const visibleItems = dept.items.filter(itemVisible);
    if (!visibleItems.length) return baseDept;

    const firstItem = visibleItems[0];
    const mainSlug = norm(firstItem.label).replace(/\s+/g, "-");
    const base = `${baseDept}/${mainSlug}`;

    const isGoFirstSub =
      MAIN_THAT_GO_TO_FIRST_SUB.has(norm(firstItem.label)) &&
      firstItem.subitems?.length;

    if (isGoFirstSub) {
      const firstVisibleSub = (firstItem.subitems || []).find(subItemVisible);
      if (firstVisibleSub) {
        const subSlug = norm(firstVisibleSub.label).replace(/\s+/g, "-");
        return `${base}/${subSlug}`;
      }
    }
    return base;
  };

  // ✅ abrir dept do usuário por padrão e redirecionar
  useEffect(() => {
    if (!sessao) return;
    const who = (sessao.departamento || "").toLowerCase();
    if (!who) return;

    if (who === "desenvolvedor") {
      setOpenDepts([]);
      return;
    }

    // encontra dept por permKey (slug)
    const dept = departmentsSource.find(
      (d) => norm(getDeptPermKey(d)) === norm(who)
    );
    if (!dept) return;

    setOpenDepts((prev) => (prev.includes(dept.name) ? prev : [dept.name]));

    const parts = location.pathname.split("/").filter(Boolean);
    const currentDeptFromPath = parts[0] || "";

    // considera dept “atual” se a URL bater no routeKey (ou no slug legando)
    const onThisDept =
      norm(currentDeptFromPath) === norm(getDeptRouteKey(dept)) ||
      norm(currentDeptFromPath) === norm(getDeptPermKey(dept));

    const hasSelectionInsideDept = onThisDept && parts.length >= 2;

    if (!hasSelectionInsideDept) {
      const firstPath = buildFirstPath(dept);
      navigate(firstPath, { replace: true });
    }
  }, [sessao, location.pathname, navigate, departmentsSource]);

  // ✅ abre dept da URL se visível e não minimizado manualmente
  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return;

    const deptFromPath = parts[0];

    // tenta achar dept por routeKey primeiro, depois por permKey
    const dept =
      departmentsSource.find(
        (d) => norm(getDeptRouteKey(d)) === norm(deptFromPath)
      ) ||
      departmentsSource.find(
        (d) => norm(getDeptPermKey(d)) === norm(deptFromPath)
      );

    if (!dept) return;

    // só abre se o usuário pode ver (por permKey)
    if (!canSeeDept(getDeptPermKey(dept))) return;

    if (
      !openDepts.includes(dept.name) &&
      !collapsedByUser.includes(dept.name)
    ) {
      setOpenDepts((prev) => [...prev, dept.name]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, userDept, departmentsSource]);

  // ✅ toggle recebendo o Department (evita derivar errado pelo "name")
  const toggleDepartment = (dept: Department) => {
    if (!canSeeDept(getDeptPermKey(dept))) return;

    setOpenDepts((prev) => {
      const isOpenDept = prev.includes(dept.name);
      if (isOpenDept) {
        setCollapsedByUser((c) =>
          c.includes(dept.name) ? c : [...c, dept.name]
        );
        return prev.filter((d) => d !== dept.name);
      } else {
        setCollapsedByUser((c) => c.filter((d) => d !== dept.name));
        return [...prev, dept.name];
      }
    });
  };

  const parts = location.pathname.split("/").filter(Boolean);
  const currentDeptFromPath = parts.length > 0 ? parts[0] : null;

  // Ripple
  const rippleRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  const setRippleRef = (key: string) => (el: HTMLSpanElement | null) => {
    if (el) rippleRefs.current.set(key, el);
    else rippleRefs.current.delete(key);
  };
  const triggerRipple = (key: string, e: React.MouseEvent) => {
    const el = rippleRefs.current.get(key);
    if (!el) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.classList.remove("ripple-anim");
    void el.offsetWidth;
    el.classList.add("ripple-anim");
  };

  return (
    <>
      {/* CSS: scroll fino, fades e ripple */}
      <style>{`
        .${LEFTBAR_CLASS} {
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.28) transparent;
          scrollbar-gutter: stable;
          -webkit-overflow-scrolling: touch;
          position: relative;
          -webkit-font-smoothing: antialiased;
          text-rendering: geometricPrecision;
          -webkit-mask-size: 100% 100%;
          mask-size: 100% 100%;
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
        }

        .${LEFTBAR_CLASS}::-webkit-scrollbar { width: 6px; }
        .${LEFTBAR_CLASS}::-webkit-scrollbar-track { background: transparent; }
        .${LEFTBAR_CLASS}::-webkit-scrollbar-thumb {
          background-color: rgba(255,255,255,0.28);
          border-radius: 8px;
        }
        .${LEFTBAR_CLASS}::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255,255,255,0.38);
        }

        .${LEFTBAR_CLASS} > * { padding-right: 2px; }

        .ripple {
          position: absolute;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255,255,255,0.35);
          transform: translate(-50%, -50%) scale(0);
          pointer-events: none;
          opacity: 0.9;
        }
        .ripple-anim { animation: ripple-scale 600ms ease-out forwards; }
        @keyframes ripple-scale {
          to { transform: translate(-50%, -50%) scale(14); opacity: 0; }
        }
      `}</style>

      {/* HEADER MOBILE */}
      {isMobile && (
        <div style={{ ...styles.mobileHeader, backgroundColor: primaryColor }}>
          <img
            src={MenuIcon}
            alt="Menu"
            style={{ width: "28px", cursor: "pointer" }}
            onClick={() => setIsOpen(true)}
          />
          <p style={styles.titleMobile}>{systemTitle}</p>
        </div>
      )}

      {/* OVERLAY */}
      {isMobile && isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      {(isOpen || !isMobile) && (
        <AnimatePresence initial={false}>
          <motion.aside
            key="sidebar"
            className={LEFTBAR_CLASS}
            style={{
              ...styles.container,
              backgroundColor: primaryColor,
              transform: isMobile
                ? isOpen
                  ? "translateX(0)"
                  : "translateX(-100%)"
                : "translateX(0)",
            }}
            initial={isMobile ? "hidden" : false}
            animate="show"
            exit="hidden"
            variants={sidebarVariants}
            onClick={(e) => e.stopPropagation()}
          >
            {isMobile && (
              <div style={{ textAlign: "right", padding: "10px" }}>
                <motion.button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✕
                </motion.button>
              </div>
            )}

            <div style={styles.inner}>
              {!isMobile && (
                <motion.div
                  style={styles.header}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                >
                  <p style={styles.title}>{systemTitle}</p>
                </motion.div>
              )}

              {/* Lista de departamentos */}
              <motion.div
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: 0.05 } },
                }}
              >
                {visibleDepartments.map((dept, i) => {
                  const routeKey = getDeptRouteKey(dept); // ✅ URL real
                  const baseDeptPath = `/${routeKey}`;

                  const isOpenBecauseOfURL =
                    currentDeptFromPath &&
                    norm(currentDeptFromPath) === norm(routeKey) &&
                    !collapsedByUser.includes(dept.name);

                  const isOpenDept =
                    openDepts.includes(dept.name) || isOpenBecauseOfURL;

                  const deptItems = dept.items.filter(itemVisible);

                  return (
                    <motion.div
                      key={dept.name}
                      variants={deptItemVariants}
                      custom={i}
                    >
                      {/* Cabeçalho do departamento */}
                      <motion.div
                        style={styles.departments}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDepartment(dept);
                        }}
                        variants={hoverGlow}
                        initial="rest"
                        whileHover="hover"
                      >
                        <span>{dept.name}</span>
                        <motion.span
                          variants={chevronVariants}
                          animate={isOpenDept ? "open" : "closed"}
                          style={{ display: "inline-flex" }}
                        >
                          <ChevronRight size={12} />
                        </motion.span>

                        <motion.div
                          aria-hidden
                          style={{
                            position: "absolute",
                            left: 0,
                            bottom: 0,
                            width: "100%",
                            height: 1,
                            background:
                              "linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0))",
                          }}
                          initial={{ scaleX: 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.25 }}
                        />
                      </motion.div>

                      {/* Submenu animado */}
                      <AnimatePresence initial={false}>
                        {isOpenDept && (
                          <motion.div
                            style={styles.submenuWrapper}
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={submenuVariants}
                          >
                            <div style={styles.submenu}>
                              {deptItems.map((item) => {
                                const mainLabel = item.label;
                                const mainSlug = norm(mainLabel).replace(
                                  /\s+/g,
                                  "-"
                                );
                                const baseMainPath = `${baseDeptPath}/${mainSlug}`;

                                const visibleSubs = (
                                  item.subitems || []
                                ).filter(subItemVisible);

                                let mainPath = baseMainPath;
                                if (
                                  MAIN_THAT_GO_TO_FIRST_SUB.has(
                                    norm(mainLabel)
                                  ) &&
                                  visibleSubs.length
                                ) {
                                  const firstSubSlug = norm(
                                    visibleSubs[0].label
                                  ).replace(/\s+/g, "-");
                                  mainPath = `${baseMainPath}/${firstSubSlug}`;
                                }

                                const isMainSelected =
                                  location.pathname.startsWith(baseMainPath);
                                const indicatorKey = `${dept.name}-${mainLabel}-indicator`;

                                return (
                                  <div
                                    key={mainLabel}
                                    style={{ position: "relative" }}
                                  >
                                    <motion.div
                                      style={{
                                        ...styles.subItem,
                                        zIndex: 1,
                                        ...(isMainSelected
                                          ? {
                                              ...styles.subItemActive,
                                              backgroundColor:
                                                menuButtonsSelection,
                                            }
                                          : {}),
                                      }}
                                      variants={hoverGlow}
                                      initial="rest"
                                      whileHover="hover"
                                      whileTap={{ scale: 0.98 }}
                                    >
                                      <span
                                        ref={setRippleRef(
                                          `main-${dept.name}-${mainLabel}`
                                        )}
                                        className="ripple"
                                      />
                                      <div
                                        style={{
                                          ...styles.subItemContent,
                                          display: "flex",
                                          alignItems: "center",
                                          textDecoration: "none",
                                        }}
                                      >
                                        <NavLink
                                          to={mainPath}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerRipple(
                                              `main-${dept.name}-${mainLabel}`,
                                              e
                                            );
                                            setCollapsedByUser((c) =>
                                              c.filter((d) => d !== dept.name)
                                            );
                                          }}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            color: "inherit",
                                            textDecoration: "none",
                                            width: "100%",
                                          }}
                                        >
                                          <OptionIcon label={mainLabel} />
                                          {renderLabelWithBadge(
                                            mainLabel,
                                            item.developed,
                                            item.migrated,
                                            item.markOrange,
                                            item.markBlue
                                          )}
                                        </NavLink>
                                      </div>
                                    </motion.div>

                                    <AnimatePresence>
                                      {isMainSelected && (
                                        <motion.div
                                          key={indicatorKey}
                                          variants={activeIndicatorVariants}
                                          initial="hidden"
                                          animate="show"
                                          exit="hidden"
                                          style={{
                                            position: "absolute",
                                            left: 0,
                                            top: 6,
                                            bottom: 6,
                                            width: 3,
                                            background:
                                              menu_buttons_selection_effect,
                                            borderRadius: 2,
                                            zIndex: 2,
                                          }}
                                        />
                                      )}
                                    </AnimatePresence>

                                    <AnimatePresence initial={false}>
                                      {visibleSubs.length > 0 && (
                                        <motion.div
                                          initial="collapsed"
                                          animate="open"
                                          exit="collapsed"
                                          variants={submenuVariants}
                                        >
                                          {visibleSubs.map((sub) => {
                                            const subSlug = norm(
                                              sub.label
                                            ).replace(/\s+/g, "-");
                                            const subPath = `${baseMainPath}/${subSlug}`;
                                            const isSubSelected =
                                              location.pathname === subPath;
                                            const subKey = `sub-${dept.name}-${mainLabel}-${sub.label}`;

                                            return (
                                              <motion.div
                                                key={sub.label}
                                                style={{
                                                  ...styles.subItem,
                                                  paddingLeft: "50px",
                                                  backgroundColor: isSubSelected
                                                    ? menuButtonsSelection
                                                    : undefined,
                                                }}
                                                variants={hoverGlow}
                                                initial="rest"
                                                whileHover="hover"
                                                whileTap={{ scale: 0.98 }}
                                              >
                                                <span
                                                  ref={setRippleRef(subKey)}
                                                  className="ripple"
                                                />
                                                <NavLink
                                                  to={subPath}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    triggerRipple(subKey, e);
                                                    setCollapsedByUser((c) =>
                                                      c.filter(
                                                        (d) => d !== dept.name
                                                      )
                                                    );
                                                  }}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    color: "inherit",
                                                    textDecoration: "none",
                                                    width: "100%",
                                                  }}
                                                >
                                                  <OptionIcon label="circle" />
                                                  <div
                                                    style={
                                                      styles.subItemContent
                                                    }
                                                  >
                                                    {renderLabelWithBadge(
                                                      sub.label,
                                                      sub.developed,
                                                      sub.migrated,
                                                      sub.markOrange,
                                                      sub.markBlue
                                                    )}
                                                  </div>
                                                </NavLink>
                                              </motion.div>
                                            );
                                          })}
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </motion.aside>
        </AnimatePresence>
      )}
    </>
  );
};

export default LeftBar;
